/**
 * What a model picker remembers, and how an entry reads in the dropdown
 * (pure — no React, no network).
 *
 * The pick is a PERSONAL VIEW PREFERENCE, exactly like dark mode
 * (core/theme.ts): one localStorage key, never the CRDT doc. Which model
 * answers your question is not board content — two writers on the same board
 * can prefer different ones, and syncing it would make picking a model an edit
 * everyone else sees.
 */
import type { AiModelEntry } from "@kola/shared";

const STORAGE_PREFIX = "kola.aiModel.";

/**
 * The empty string is "no explicit pick" — run on whatever the admin has set
 * as this function's default. It is the value a picker starts on, and the one
 * it falls back to when a remembered model leaves the list.
 */
export const DEFAULT_MODEL = "";

/**
 * The remembered model for one AI function, or `DEFAULT_MODEL`. Reads
 * defensively: localStorage throws in a locked-down browser, and a value
 * written by an older build should degrade to the default rather than to an
 * exception.
 */
export function storedModel(functionKey: string): string {
  try {
    return window.localStorage.getItem(STORAGE_PREFIX + functionKey) ?? DEFAULT_MODEL;
  } catch {
    return DEFAULT_MODEL;
  }
}

/** Remember (or, with `DEFAULT_MODEL`, forget) the pick for one AI function. */
export function rememberModel(functionKey: string, model: string): void {
  try {
    if (model === DEFAULT_MODEL) window.localStorage.removeItem(STORAGE_PREFIX + functionKey);
    else window.localStorage.setItem(STORAGE_PREFIX + functionKey, model);
  } catch {
    // A browser that refuses storage still gets the pick for this panel's
    // lifetime — it just won't be there next time. Not worth a warning.
  }
}

/** How the first option reads: what a run with no explicit pick will use. */
export function defaultOptionLabel(defaultModel: string | undefined): string {
  return defaultModel === undefined || defaultModel === ""
    ? "Default"
    : `Default · ${defaultModel}`;
}

/**
 * Row id of the stand-in `withCurrentModel` mints — a marker, not a real row,
 * and never an id the server could issue (it has no `aim_` prefix).
 */
const NOT_LISTED_PREFIX = "not-listed:";

/** One dropdown line: the admin's nickname when they gave one, else the id. */
export function entryLabel(entry: AiModelEntry): string {
  if (entry.id.startsWith(NOT_LISTED_PREFIX)) return `${entry.modelId} · not on the list`;
  return entry.label.trim().length > 0 ? `${entry.label} · ${entry.modelId}` : entry.modelId;
}

/**
 * The list with the CURRENT pick guaranteed present, so a model that is not on
 * the list still shows as selected instead of the dropdown silently snapping
 * to something else and misreporting what the next run will use. Two ways to
 * get here, and the stand-in reads correctly for both: a remembered pick an
 * admin has since removed, and a per-function default that comes from server
 * config and was never added to the list.
 */
export function withCurrentModel(
  entries: readonly AiModelEntry[],
  current: string,
): AiModelEntry[] {
  if (current === DEFAULT_MODEL || entries.some((entry) => entry.modelId === current)) {
    return [...entries];
  }
  return [
    ...entries,
    {
      id: `${NOT_LISTED_PREFIX}${current}`,
      modelId: current,
      label: "",
      kind: "text",
      createdAt: "",
      updatedAt: "",
      updatedBy: "",
    },
  ];
}
